import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";

import {
  getBillDetailsV2,
  approveBillV2,
  cancelBillV2,
  getPaymentsV2,
  BillV2,
  PaymentV2,
} from "../../utils/billingServiceV2";
import moment from "moment";
import Spinner from "../../components/spinner/Spinner";
import StatusModal from "../../components/modals/StatusModal";
import ConfirmationModal from "../../components/modals/ConfirmationModal";

interface BillDetailsModalProps {
  billId: string | null;
  onClose: () => void;
  onStatusChange: () => void;
}

const BillDetailsModal: React.FC<BillDetailsModalProps> = ({
  billId,
  onClose,
  onStatusChange,
}) => {
  const navigate = useNavigate();
  const [bill, setBill] = useState<BillV2 | null>(null);
  const [payments, setPayments] = useState<PaymentV2[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusModal, setStatusModal] = useState<{
    open: boolean;
    type: "success" | "error" | "warning";
    message: string;
    title?: string;
  }>({
    open: false,
    type: "success",
    message: "",
  });
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    title: string;
    body: string;
    action: "approve" | "cancel" | null;
  }>({
    open: false,
    title: "",
    body: "",
    action: null,
  });

  useEffect(() => {
    // Reset modal states when billId changes (reopening or closing)
    setStatusModal({
      open: false,
      type: "success",
      message: "",
    });
    setConfirmModal({
      open: false,
      title: "",
      body: "",
      action: null,
    });

    if (billId) {
      const fetchDetails = async () => {
        setLoading(true);
        try {
          const res = await getBillDetailsV2(billId);
          if (res && res.success) {
            setBill(res.data);
          }
          const payRes = await getPaymentsV2({ billId });
          if (payRes && payRes.success) {
            setPayments(payRes.data);
          }
        } catch (err) {
          console.error("Error fetching bill details", err);
        } finally {
          setLoading(false);
        }
      };
      fetchDetails();
    } else {
      setBill(null);
    }
  }, [billId]);

  const handleApprove = async () => {
    if (!billId) return;
    setConfirmModal({ open: false, title: "", body: "", action: null });
    setActionLoading(true);
    try {
      const res = await approveBillV2(billId, {
        approvedBy: "00000000-0000-0000-0000-000000000000",
      });
      if (res && res.success) {
        setStatusModal({
          open: true,
          type: "success",
          title: "Approved",
          message: "Bill approved successfully",
        });
        onStatusChange();
      } else {
        throw res;
      }
    } catch (err: any) {
      setStatusModal({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to approve. Please try again.",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!billId) return;
    setConfirmModal({ open: false, title: "", body: "", action: null });
    setActionLoading(true);
    try {
      const res = await cancelBillV2(billId);
      if (res && res.success) {
        setStatusModal({
          open: true,
          type: "success",
          title: "Cancelled",
          message: "Bill cancelled successfully",
        });
        onStatusChange();
      } else {
        throw res;
      }
    } catch (err: any) {
      setStatusModal({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to cancel. Please try again.",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const renderDetailField = (label: string, value: any, color?: string) => (
    <Box>
      <Typography
        variant="caption"
        sx={{
          color: "#64748B",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          fontSize: "10px",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: color || "#1E293B",
          fontWeight: 600,
          mt: 0.25,
          fontSize: "13px",
        }}
      >
        {value === null || value === undefined || value === "" ? "-" : value}
      </Typography>
    </Box>
  );

  const formatCurrency = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null) return "-";
    return amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  if (!billId) return null;

  return (
    <Dialog
      open={!!billId}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          bgcolor: "#FFFFFF",
          borderRadius: "12px",
          color: "#1E293B",
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
        },
      }}
    >
      <DialogTitle sx={{ p: 2, borderBottom: "1px solid #F1F5F9" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Bill {bill?.billNumber || "..."}
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {bill?.status && (
              <Chip
                label={bill.status}
                size="small"
                sx={{
                  bgcolor:
                    bill.status?.toLowerCase() === "draft"
                      ? "rgba(148, 163, 184, 0.1)"
                      : bill.status?.toLowerCase() === "approved"
                        ? "rgba(16, 185, 129, 0.1)"
                        : "rgba(239, 68, 68, 0.1)",
                  color:
                    bill.status?.toLowerCase() === "draft"
                      ? "#64748B"
                      : bill.status?.toLowerCase() === "approved"
                        ? "#10B981"
                        : "#EF4444",
                  fontWeight: 700,
                  fontSize: "10px",
                  borderRadius: "4px",
                  height: "20px",
                }}
              />
            )}
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ color: "#64748B" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, bgcolor: "transparent" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <Spinner />
          </Box>
        ) : bill ? (
          <Box>
            {/* Section: Bill Information */}
            <Typography
              variant="caption"
              sx={{
                color: "#1064ED",
                fontWeight: 800,
                mb: 1.5,
                display: "block",
                letterSpacing: "1px",
              }}
            >
              BILL INFORMATION
            </Typography>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1.5}>
                  {renderDetailField("CUSTOMER", bill.customerName)}
                  {renderDetailField("SITE", bill.siteName)}
                  {renderDetailField(
                    "PERIOD START",
                    bill.billingPeriodStart
                      ? moment(bill.billingPeriodStart).format(
                          "DD MMM YYYY, HH:mm:ss",
                        )
                      : "-",
                  )}
                  {renderDetailField(
                    "DUE DATE",
                    bill.dueDate
                      ? moment(bill.dueDate).format("DD MMM YYYY, HH:mm:ss")
                      : "-",
                  )}
                  {renderDetailField(
                    "CREATED",
                    bill.createdAt
                      ? moment(bill.createdAt).format("DD MMM YYYY, HH:mm:ss")
                      : "-",
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1.5}>
                  {renderDetailField("CONTRACT", bill.contractNumber)}
                  {renderDetailField("BILLING TYPE", bill.billingType)}
                  {renderDetailField(
                    "PERIOD END",
                    bill.billingPeriodEnd
                      ? moment(bill.billingPeriodEnd).format(
                          "DD MMM YYYY, HH:mm:ss",
                        )
                      : "-",
                  )}
                  {bill.tariffId && (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#64748B",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          fontSize: "10px",
                        }}
                      >
                        TARIFF
                      </Typography>
                      <Box sx={{ mt: -0.5 }}>
                        <Link
                          to={`/new-tariffs?id=${bill.tariffId}`}
                          style={{
                            color: "#1064ED",
                            fontWeight: 700,
                            fontSize: "15px",
                          }}
                        >
                          {bill.tariffName || "View Details"}
                        </Link>
                      </Box>
                    </Box>
                  )}
                  {renderDetailField(
                    "LAST READ RECEIVED",
                    bill.lastReadTimestamp
                      ? moment(bill.lastReadTimestamp).format(
                          "DD MMM YYYY, HH:mm:ss",
                        )
                      : "-",
                  )}
                </Stack>
              </Grid>
            </Grid>

            {/* Section: Meter Reads */}
            <Typography
              variant="caption"
              sx={{
                color: "#1064ED",
                fontWeight: 800,
                mb: 1.5,
                display: "block",
                letterSpacing: "1px",
              }}
            >
              METER READS
            </Typography>
            <Grid
              container
              spacing={1}
              mb={1}
              sx={{
                p: 1,
                bgcolor: "#F8FAFC",
                borderRadius: "8px",
                border: "1px solid #E2E8F0",
              }}
            >
              <Grid item xs={4}>
                {renderDetailField(
                  "PREVIOUS READING",
                  bill.previousReading != null
                    ? `${bill.previousReading} kWh`
                    : "-",
                )}
              </Grid>
              <Grid item xs={4}>
                {renderDetailField(
                  "CURRENT READING",
                  bill.currentReading != null
                    ? `${bill.currentReading} kWh`
                    : "-",
                )}
              </Grid>
              <Grid item xs={4}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#64748B",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontSize: "10px",
                    }}
                  >
                    CONSUMPTION
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#1E293B",
                      fontWeight: 800,
                      mt: 0.25,
                      fontSize: "14px",
                    }}
                  >
                    {bill.totalConsumption != null
                      ? `${bill.totalConsumption} kWh`
                      : "-"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Section: Amounts */}
            <Typography
              variant="caption"
              sx={{
                color: "#1064ED",
                fontWeight: 800,
                mb: 1.5,
                display: "block",
                letterSpacing: "1px",
              }}
            >
              AMOUNTS
            </Typography>
            <Grid
              container
              spacing={1}
              mb={1}
              sx={{
                p: 1.5,
                bgcolor: "#F8FAFC",
                borderRadius: "8px",
                border: "1px solid #E2E8F0",
              }}
            >
              <Grid item xs={6}>
                {renderDetailField(
                  "TOTAL CONSUMPTION",
                  bill.totalConsumption != null
                    ? `${bill.totalConsumption} kWh`
                    : "-",
                )}
              </Grid>
              <Grid item xs={6}>
                {renderDetailField(
                  "TOTAL AMOUNT",
                  `₹${formatCurrency(bill.totalAmount != null ? bill.totalAmount * -1 : null)}`,
                )}
              </Grid>
              <Grid item xs={6}>
                {renderDetailField(
                  "TAX",
                  `₹${formatCurrency(bill.taxAmount != null ? bill.taxAmount * -1 : null)}`,
                )}
              </Grid>
              <Grid item xs={6}>
                {renderDetailField(
                  "ADJUSTMENTS",
                  `₹${formatCurrency(bill.adjustmentAmount != null ? bill.adjustmentAmount * -1 : null)}`,
                )}
              </Grid>
              <Grid item xs={6}>
                {renderDetailField(
                  "PREVIOUS BALANCE",
                  `₹${formatCurrency(bill.previousBalance != null ? bill.previousBalance * -1 : null)}`,
                )}
              </Grid>
              <Grid item xs={6}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#1064ED",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "10px",
                    }}
                  >
                    NET AMOUNT
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ color: "#1E293B", fontWeight: 800 }}
                  >
                    ₹
                    {formatCurrency(
                      bill.netAmount != null ? bill.netAmount * -1 : null,
                    )}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Section: Line Items */}
            <Typography
              variant="caption"
              sx={{
                color: "#1064ED",
                fontWeight: 800,
                mb: 1.5,
                display: "block",
                letterSpacing: "1px",
              }}
            >
              LINE ITEMS ({bill.lineItems?.length || 0})
            </Typography>
            <TableContainer
              sx={{
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                overflow: "hidden",
                bgcolor: "#FFFFFF",
              }}
            >
              <Table size="small">
                <TableHead sx={{ bgcolor: "#F1F5F9" }}>
                  <TableRow>
                    <TableCell
                      sx={{
                        color: "#64748B",
                        fontWeight: 700,
                        py: 1,
                        fontSize: "11px",
                      }}
                    >
                      DESCRIPTION
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#64748B",
                        fontWeight: 700,
                        py: 1,
                        fontSize: "11px",
                      }}
                    >
                      METER/SITE
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: "#64748B",
                        fontWeight: 700,
                        py: 1,
                        fontSize: "11px",
                      }}
                    >
                      CONS.
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: "#64748B",
                        fontWeight: 700,
                        py: 1,
                        fontSize: "11px",
                      }}
                    >
                      RATE
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: "#64748B",
                        fontWeight: 700,
                        py: 1,
                        fontSize: "11px",
                      }}
                    >
                      AMOUNT
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        color: "#64748B",
                        fontWeight: 700,
                        py: 1,
                        fontSize: "11px",
                      }}
                    >
                      SLAB
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bill.lineItems?.map((item) => (
                    <TableRow
                      key={item.id}
                      sx={{ "&:hover": { bgcolor: "#F8FAFC" } }}
                    >
                      <TableCell sx={{ color: "#1E293B", py: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: "13px" }}>
                          {item.description || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: "#334155" }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, fontSize: "12px" }}
                        >
                          {item.meterNumber || "-"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "#64748B", fontSize: "10px" }}
                        >
                          {item.siteName || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: "#1E293B", fontSize: "13px" }}
                      >
                        {item.consumption !== undefined
                          ? item.consumption
                          : "-"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: "#1E293B", fontSize: "13px" }}
                      >
                        {item.rate !== undefined && item.rate !== null
                          ? `₹${item.rate}`
                          : "-"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: "#1E293B",
                          fontWeight: 700,
                          fontSize: "13px",
                        }}
                      >
                        ₹
                        {formatCurrency(
                          item.amount != null ? item.amount : null,
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={item.tariffSlabName || "-"}
                          size="small"
                          sx={{
                            bgcolor: "#F1F5F9",
                            color: "#475569",
                            fontWeight: 600,
                            borderRadius: "3px",
                            height: "18px",
                            fontSize: "10px",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Summary Row */}
                  <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                    <TableCell
                      colSpan={4}
                      align="right"
                      sx={{ color: "#64748B", py: 1, fontSize: "11px" }}
                    >
                      Total
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: "#1E293B",
                        fontWeight: 800,
                        fontSize: "14px",
                      }}
                    >
                      ₹
                      {formatCurrency(
                        bill.netAmount != null ? bill.netAmount * -1 : null,
                      )}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Typography
              variant="caption"
              sx={{
                color: "#94A3B8",
                mt: 2,
                display: "block",
                textAlign: "left",
                fontSize: "10px",
              }}
            >
              Updated:{" "}
              {bill.updatedAt
                ? moment(bill.updatedAt).format("DD MMM YYYY, HH:mm:ss")
                : "-"}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "#64748B" }}>
              Bill not found.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: "#F8FAFC",
          borderTop: "1px solid #E2E8F0",
        }}
      >
        <Button
          onClick={onClose}
          size="small"
          sx={{ color: "#64748B", textTransform: "none", fontWeight: 600 }}
        >
          Close
        </Button>
        {bill && bill.status?.toLowerCase() === "draft" && (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              gap: 1.5,
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() =>
                setConfirmModal({
                  open: true,
                  title: "Cancel Bill",
                  body: "Are you sure you want to cancel this bill? This action cannot be undone.",
                  action: "cancel",
                })
              }
              sx={{
                textTransform: "none",
                borderRadius: "6px",
                fontWeight: 600,
              }}
              disabled={actionLoading}
            >
              Cancel Bill
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() =>
                setConfirmModal({
                  open: true,
                  title: "Approve Bill",
                  body: "Approving this bill will finalize the amounts and notify the customer.",
                  action: "approve",
                })
              }
              sx={{
                textTransform: "none",
                borderRadius: "6px",
                bgcolor: "#10B981",
                "&:hover": { bgcolor: "#059669" },
                px: 3,
                fontWeight: 600,
              }}
              disabled={actionLoading}
            >
              Approve Bill
            </Button>
          </Box>
        )}
      </DialogActions>

      <ConfirmationModal
        confirmModal={confirmModal.open}
        title={confirmModal.title}
        body={confirmModal.body}
        onClose={() =>
          setConfirmModal({ ...confirmModal, open: false, action: null })
        }
        onClick={
          confirmModal.action === "approve" ? handleApprove : handleCancel
        }
        isLoading={actionLoading}
      />

      <StatusModal
        open={statusModal.open}
        type={statusModal.type}
        title={statusModal.title}
        message={statusModal.message}
        onClose={() => setStatusModal({ ...statusModal, open: false })}
      />
    </Dialog>
  );
};

export default BillDetailsModal;
